import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Modal, Button, Space, message } from "antd";
import type { FormInstance } from "antd/es/form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DynamicForm from "@/components/DynamicForm";
import ProcessTimeline from "@/components/ProcessTimeline";
import type { ProcessTimelineItem } from "@/components/ProcessTimeline";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import { getPersonnelFormItems } from "./config/formConfig";
import { getPersonnelInfo } from "@/pages/supplier/SupplierException/utils/fillFormFields";
import {
  generateProcessCode,
  startMeetingApproval,
  getRiskRegisterInfoById,
  getHistoryByInsId,
} from "@/apis/supplier";
import { passFlow } from "@/apis/flow.api";
import { useMaterialData } from "../../hooks/useMaterialData";
import { useSupplierData } from "../../hooks/useSupplierData";
import { useSupplierSelectModal } from "./hooks/useSupplierSelectModal";
import type { PersonnelInfo, RegisterRiskFormData } from "./types";
import styles from "./index.module.less";

// ==================== 常量配置 ====================

// ==================== 渲染函数 ====================

/**
 * 获取风险登记Modal标题
 */
const getModalTitle = (
  t: any,
  processCode: string = "",
  isEditMode: boolean = false,
  readOnly: boolean = false,
  isNotifyMode: boolean = false,
) => {
  let title = "";
  if (isNotifyMode) {
    title = t("通知供应商风险");
  } else if (readOnly) {
    title = t("查看供应商风险");
  } else {
    title = isEditMode ? t("编辑供应商风险") : t("登记供应商风险");
  }
  return (
    <div className={styles.titleContainer}>
      <span>{title}</span>
      <div className={styles.titleDivider} />
      <span className={styles.titleProcessCode}>
        {t("单号")}：{processCode || t("加载中")}
      </span>
    </div>
  );
};

// ==================== 类型定义 ====================
interface RegisterRiskModalProps {
  visible: boolean;
  editingId?: number | string;
  readOnly?: boolean;
  isNotifyMode?: boolean;
  businessStatus?: number;
  onSubmit?: (data: RegisterRiskFormData) => void;
  onClose?: () => void;
  onNotify?: () => void;
}

// ==================== 主组件 ====================
const RegisterRiskModal: React.FC<RegisterRiskModalProps> = ({
  visible,
  editingId,
  readOnly = false,
  isNotifyMode = false,
  businessStatus,
  onSubmit,
  onClose,
  onNotify,
}) => {
  const { t } = useTranslation("supplierRiskLevel");
  const { t: tCommon } = useTranslation("common");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [personnelInfo, setPersonnelInfo] = useState<PersonnelInfo | null>(
    null,
  );
  const [processCode, setProcessCode] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [editDataId, setEditDataId] = useState<number | null>(null);
  const [timelineData, setTimelineData] = useState<ProcessTimelineItem[]>([]);
  const formRef = useRef<FormInstance>(null);
  const {
    materialOptions,
    getMaterialInfo,
    loading: materialLoading,
    handleMaterialSearch,
    fetchMaterialData,
  } = useMaterialData();
  const {
    supplierOptions,
    loading: supplierLoading,
    handleSupplierSearch,
    getSupplierInfo,
  } = useSupplierData();

  // 使用Hook管理供应商选择弹框
  const {
    modalOpen: supplierModalOpen,
    handleOpenModal: handleOpenSupplierModal,
    handleCloseModal: handleCloseSupplierModal,
  } = useSupplierSelectModal();

  // 模态框打开时获取用户信息和初始物料数据，并生成单号或加载编辑数据
  useEffect(() => {
    if (visible) {
      console.log("🔍 RegisterRiskModal - 接收参数:", {
        editingId,
        readOnly,
        isNotifyMode,
        businessStatus,
      });
      setIsEditMode(!!editingId);
      const info = getPersonnelInfo();
      setPersonnelInfo(info);

      // 编辑模式：加载风险登记详情数据
      if (editingId) {
        const fetchEditData = async () => {
          try {
            const response = await getRiskRegisterInfoById(editingId);
            console.log("【编辑数据】获取风险登记详情:", response);

            if (response?.data?.data) {
              const data = response.data.data as any;
              // 直接使用返回的单号
              setProcessCode(data.processCode || "");
              // 存储 taskId 用于提交时调用 /view/pass 接口
              setTaskId(data.taskId || null);
              // 存储编辑数据的 id，用于提交时传入 srmSupplierRiskRegisterDTO
              setEditDataId(data.id || null);

              // 为物料类别和二级物料类别创建选项
              const materialCategoryOptions = data.materialCategoryName
                ? [
                    {
                      label: data.materialCategoryName,
                      value: data.materialCategoryValue,
                    },
                  ]
                : [];
              const materialTwoCategoryOptions = data.materialTwoCategoryName
                ? [
                    {
                      label: data.materialTwoCategoryName,
                      value: data.materialTwoCategoryValue,
                    },
                  ]
                : [];

              setMaterialCategoryOptions(materialCategoryOptions);
              setMaterialTwoCategoryOptions(materialTwoCategoryOptions);

              // 如果有供应商ID，获取物料列表
              if (data.supplierUserId) {
                fetchMaterialData("", data.supplierUserId);
              }

              // ==================== 获取流程动态 ====================
              if (data.insId && (readOnly || isNotifyMode)) {
                console.log("开始获取历史记录，insId:", data.insId);
                getHistoryByInsId(data.insId)
                  .then((historyResponse) => {
                    console.log("历史记录响应:", historyResponse);
                    if (
                      historyResponse?.data?.code === 200 &&
                      Array.isArray(historyResponse?.data?.data)
                    ) {
                      const timeline = historyResponse.data.data.map(
                        (item: any) => ({
                          approver: item.approver,
                          flowStatus: item.flowStatus,
                          postName: item.postName,
                          createTime: item.createTime,
                          message: item.message,
                          avatarUrl:
                            item.avatarUrl ||
                            "https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg",
                        }),
                      );
                      console.log("转换后的timeline数据:", timeline);
                      setTimelineData(timeline);
                    }
                  })
                  .catch((historyError) => {
                    console.error("获取历史记录失败:", historyError);
                  });
              }

              // ==================== 处理文件字段回显 ====================
              // 将 srmSupplierFlowBusinessFiles 转换为 fileIds 字段（Upload 组件需要的格式）
              // 过滤掉 fileType 为 "solutionFile" 的文件
              console.log("原始文件列表:", data.srmSupplierFlowBusinessFiles);
              const fileIds = (data.srmSupplierFlowBusinessFiles || [])
                .filter((file: any) => {
                  const shouldInclude = file.fileType !== "solutionFile";
                  console.log(
                    `文件 ${file.fileName}: fileType="${file.fileType}", 是否包含=${shouldInclude}`,
                  );
                  return shouldInclude;
                })
                .map((file: any) => ({
                  uid: file.id || file.fileUrl,
                  name:
                    file.fileName || file.fileUrl.split("/").pop() || "文件",
                  status: "done" as const,
                  url: file.fileUrl,
                  fileId: file.id,
                  originFileObj: undefined,
                }));
              console.log("过滤后的文件列表:", fileIds);

              // 处理日期字段转换
              const formData = {
                ...data,
                fileIds: fileIds.length > 0 ? fileIds : undefined,
                discoveryDate: data.discoveryDate
                  ? dayjs(data.discoveryDate)
                  : undefined,
                expectedResolutionDate: data.expectedResolutionDate
                  ? dayjs(data.expectedResolutionDate)
                  : undefined,
              };

              // 延迟设置表单值，确保表单已渲染
              setTimeout(() => {
                formRef.current?.setFieldsValue(formData);
              }, 0);
            } else {
            }
          } catch (error) {}
        };

        fetchEditData();
      } else {
        // 新增模式：生成单号
        const fetchProcessCode = async () => {
          try {
            const response = await generateProcessCode(
              "SUPPLIER_RISK_REGISTER",
            );
            console.log("生成单号 API 响应:", response);
            let code = response?.data?.data;

            // 如果返回的数据包含"加载中"等文本，需要提取实际的单号
            if (typeof code === "string") {
              // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
              code = code.replace(/^[^A-Z0-9]*/, "").trim();
              if (code) {
                setProcessCode(code);
              } else {
                setProcessCode(t("生成失败"));
              }
            } else {
              setProcessCode(t("生成失败"));
            }
          } catch (error) {
            console.error("生成单号失败:", error);
            setProcessCode(t("生成失败"));
          }
        };

        fetchProcessCode();
      }
    } else {
      // 模态框关闭时清空数据
      setProcessCode("");
      setTaskId(null);
      setEditDataId(null);
      setIsEditMode(false);
      setTimelineData([]);
    }
  }, [
    visible,
    editingId,
    fetchMaterialData,
    readOnly,
    isNotifyMode,
    businessStatus,
    t,
  ]);

  // 处理供应商选择变化，自动填充供应商相关信息
  const handleSupplierChange = useCallback(
    (supplierUserCode: string) => {
      const supplierInfo = getSupplierInfo(supplierUserCode);
      if (supplierInfo && formRef.current) {
        // 为物料类别和二级物料类别创建选项
        const materialCategoryOptions = [
          {
            label: supplierInfo.materialCategoryName,
            value: supplierInfo.materialCategoryValue,
          },
        ];
        const materialTwoCategoryOptions = [
          {
            label: supplierInfo.materialTwoCategoryName,
            value: supplierInfo.materialTwoCategoryValue,
          },
        ];

        formRef.current.setFieldsValue({
          supplierUserId: supplierInfo.id || "",
          supplierUserName: supplierInfo.supplierUserName,
          supplierUsUserName: supplierInfo.supplierUsUserName,
          materialCategoryValue: supplierInfo.materialCategoryValue,
          materialTwoCategoryValue: supplierInfo.materialTwoCategoryValue,
        });

        // 存储物料类别选项用于表单显示
        setMaterialCategoryOptions(materialCategoryOptions);
        setMaterialTwoCategoryOptions(materialTwoCategoryOptions);

        // 选择供应商后，使用供应商ID查询对应的物料列表
        if (supplierInfo.id) {
          fetchMaterialData("", supplierInfo.id);
        }
      }
    },
    [getSupplierInfo, fetchMaterialData],
  );

  // 处理供应商选择弹框的选择确认
  const handleSupplierSelect = useCallback(
    (supplier: SupplierUserSimpleListItem | undefined) => {
      if (supplier) {
        // 设置供应商编号字段，触发 onChange 事件来填充其他信息
        formRef.current?.setFieldsValue({
          supplierUserCode: supplier.supplierCode || "",
          supplierUserId: supplier.id || "",
          supplierUserName: supplier.supplierName || "",
          supplierUsUserName: supplier.supplierUsName || "",
        });

        // 为物料类别和二级物料类别创建选项
        // 从 supplier 对象中获取这些信息
        const materialCategoryOptions = supplier.materialCategoryName
          ? [
              {
                label: supplier.materialCategoryName,
                value: supplier.materialCategoryValue || "",
              },
            ]
          : [];
        const materialTwoCategoryOptions = supplier.materialTwoCategoryName
          ? [
              {
                label: supplier.materialTwoCategoryName,
                value: supplier.materialTwoCategoryValue || "",
              },
            ]
          : [];

        setMaterialCategoryOptions(materialCategoryOptions);
        setMaterialTwoCategoryOptions(materialTwoCategoryOptions);

        // 如果有供应商ID，获取物料列表
        if (supplier.id) {
          fetchMaterialData("", supplier.id);
        }
      }
      handleCloseSupplierModal(supplier);
    },
    [handleCloseSupplierModal, fetchMaterialData],
  );

  // 处理物料编号选择变化，自动填充物料相关信息
  const handleMaterialChange = useCallback(
    (materialCode: string) => {
      const materialInfo = getMaterialInfo(materialCode);
      if (materialInfo && formRef.current) {
        formRef.current.setFieldsValue({
          materialName: materialInfo.materialName,
        });
      }
    },
    [getMaterialInfo],
  );

  // 存储物料类别和二级物料类别的选项
  const [materialCategoryOptions, setMaterialCategoryOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [materialTwoCategoryOptions, setMaterialTwoCategoryOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  // 获取表单项配置，并动态设置物料选项和供应商选项
  const formItems = useMemo(() => {
    const RISK_TYPE_OPTIONS = [
      { label: t("质量风险"), value: "A" },
      { label: t("交付风险"), value: "B" },
      { label: t("财务风险"), value: "C" },
      { label: t("成本风险"), value: "D" },
      { label: t("注册风险"), value: "E" },
      { label: t("其他风险"), value: "F" },
    ];

    const RISK_LEVEL_ASSESSMENT_OPTIONS = [
      { label: t("高"), value: "1" },
      { label: t("中"), value: "2" },
      { label: t("低"), value: "3" },
    ];

    const isReadOnly = readOnly || isNotifyMode || businessStatus === 1;
    const items = getPersonnelFormItems(
      t,
      personnelInfo ?? undefined,
      businessStatus,
      isReadOnly,
      handleOpenSupplierModal,
    );
    // 找到物料编号和供应商编号字段并更新其选项
    return items.map((item) => {
      if (item.name === "materialCode") {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialOptions,
            loading: materialLoading,
            onSearch: handleMaterialSearch,
            onChange: handleMaterialChange,
          },
        };
      }
      if (item.name === "supplierUserCode") {
        // 供应商编号已改为可点击的 Input 组件，不需要额外配置
        return item;
      }
      if (item.name === "materialCategoryValue") {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialCategoryOptions,
          },
        };
      }
      if (item.name === "materialTwoCategoryValue") {
        return {
          ...item,
          widget: {
            ...item.widget,
            options: materialTwoCategoryOptions,
          },
        };
      }
      if (item.name === "riskTypeValue") {
        return {
          ...item,
          widget: {
            ...item.widget,
            onChange: (value: string) => {
              if (formRef.current) {
                const riskTypeOption = RISK_TYPE_OPTIONS.find(
                  (opt) => opt.value === value,
                );
                formRef.current.setFieldsValue({
                  riskTypeName: riskTypeOption?.label || "",
                });
              }
            },
          },
        };
      }
      if (item.name === "riskLevelAssessmentValue") {
        return {
          ...item,
          widget: {
            ...item.widget,
            onChange: (value: string) => {
              if (formRef.current) {
                const riskLevelOption = RISK_LEVEL_ASSESSMENT_OPTIONS.find(
                  (opt) => opt.value === value,
                );
                formRef.current.setFieldsValue({
                  riskLevelAssessmentName: riskLevelOption?.label || "",
                });
              }
            },
          },
        };
      }
      return item;
    });
  }, [
    t,
    personnelInfo,
    businessStatus,
    readOnly,
    isNotifyMode,
    materialOptions,
    materialLoading,
    handleMaterialSearch,
    handleMaterialChange,
    handleOpenSupplierModal,
    materialCategoryOptions,
    materialTwoCategoryOptions,
  ]);

  // 处理表单提交
  const handleSubmit = async (formValue: any) => {
    try {
      setSubmitLoading(true);

      // 先验证表单
      if (!formRef.current) {
        message.error(t("表单数据获取失败"));
        setSubmitLoading(false);
        return;
      }

      // 验证表单字段
      await formRef.current.validateFields();

      // 格式化日期字段：将 dayjs 对象转换为 YYYY-MM-DD 格式字符串
      if (formValue.discoveryDate && dayjs.isDayjs(formValue.discoveryDate)) {
        formValue.discoveryDate = formValue.discoveryDate.format("YYYY-MM-DD");
      }
      if (
        formValue.expectedResolutionDate &&
        dayjs.isDayjs(formValue.expectedResolutionDate)
      ) {
        formValue.expectedResolutionDate =
          formValue.expectedResolutionDate.format("YYYY-MM-DD");
      }

      // 处理 fileIds：提取文件对象数组中的 fileId，生成纯ID数组
      if (Array.isArray(formValue.fileIds) && formValue.fileIds.length > 0) {
        formValue.fileIds = formValue.fileIds
          .map((file: any) => file.fileId)
          .filter((id: any) => id); // 过滤掉空值
      }

      // businessStatus === 3 时，根据评估意见判断 skipType
      const skipType =
        businessStatus === 3 && formValue.assessmentOpinion === "agree"
          ? "PASS"
          : "REJECT";

      // 计算 processingStatus：businessStatus === 3 时根据 skipType 判断
      const processingStatus =
        businessStatus === 3 ? (skipType === "PASS" ? 3 : 4) : 1;

      const submitData = {
        ...formValue,
        processCode: processCode,
        processingStatus: processingStatus,
        purchaserUserId: formValue.purchaserUserId || "",
        purchaseUserDeptNo: formValue.purchaseUserDeptNo || "",
        purchaseUserPostNo: formValue.purchaseUserPostNo || "",
        materialCategoryName: materialCategoryOptions[0]?.label || "",
        materialCategoryValue: formValue.materialCategoryValue || "",
        materialTwoCategoryName: materialTwoCategoryOptions[0]?.label || "",
        materialTwoCategoryValue: formValue.materialTwoCategoryValue || "",
        riskLevelAssessmentName: formValue.riskLevelAssessmentName || "",
        riskTypeName: formValue.riskTypeName || "",
        supplierUserId: formValue.supplierUserId || "",
      };

      // 编辑模式或 businessStatus === 3：调用 /view/pass 接口提交审批
      if ((isEditMode && taskId) || (businessStatus === 3 && taskId)) {
        console.log("【编辑/审批提交】调用 /view/pass 接口，taskId:", taskId);
        const passData: any = {
          flowCode: "SUPPLIER_RISK_REGISTER",
          skipType: skipType,
          formNo: processCode,
          variable: {
            supplierUserId: formValue.supplierUserId,
          },
          srmSupplierRiskRegisterDTO: {
            ...submitData,
            id: editDataId, // 添加编辑数据的 id
          },
        };

        // businessStatus === 3 时，添加 message 字段（意见说明）
        if (businessStatus === 3) {
          passData.message = formValue.assessmentComments || "";
        }

        const response = await passFlow(taskId, passData);
        console.log("【审批响应】", response);
        message.success(t("提交成功"));
        handleModalClose();
      } else {
        // 新增模式：调用 /view/start 接口发起流程
        console.log("【新增提交】调用 /view/start 接口");
        const newSubmitData = {
          flowStatus: 1, // 提交状态
          flowCode: "SUPPLIER_RISK_REGISTER",
          formNo: `${processCode}`,
          variable: {
            supplierUserId: formValue.supplierUserId,
          },
          srmSupplierRiskRegisterDTO: submitData,
        };

        console.log("【提交数据】", newSubmitData);
        const response = await startMeetingApproval(newSubmitData);
        console.log("【流程启动响应】", response);

        // 调用 onSubmit 回调，通知父组件刷新列表
        onSubmit?.(submitData);
        message.success(t("提交成功"));
      }
    } catch (error) {
      console.error("提交失败:", error);
      message.error(t("提交失败"));
    } finally {
      setSubmitLoading(false);
    }
  };

  // 处理表单暂存
  const handleSaveDraft = async () => {
    try {
      setDraftLoading(true);
      if (formRef.current) {
        let formValue = formRef.current.getFieldsValue();

        // 格式化日期字段：将 dayjs 对象转换为 YYYY-MM-DD 格式字符串
        if (formValue.discoveryDate && dayjs.isDayjs(formValue.discoveryDate)) {
          formValue.discoveryDate =
            formValue.discoveryDate.format("YYYY-MM-DD");
        }
        if (
          formValue.expectedResolutionDate &&
          dayjs.isDayjs(formValue.expectedResolutionDate)
        ) {
          formValue.expectedResolutionDate =
            formValue.expectedResolutionDate.format("YYYY-MM-DD");
        }

        // 处理 fileIds：提取文件对象数组中的 fileId，生成纯ID数组
        if (Array.isArray(formValue.fileIds) && formValue.fileIds.length > 0) {
          formValue.fileIds = formValue.fileIds
            .map((file: any) => file.fileId)
            .filter((id: any) => id); // 过滤掉空值
        }

        console.log("formValue", formValue);
        const submitData = {
          ...formValue,
          processCode: processCode,
          purchaserUserId: formValue.purchaserUserId || "",
          purchaseUserDeptNo: formValue.purchaseUserDeptNo || "",
          purchaseUserPostNo: formValue.purchaseUserPostNo || "",
          materialCategoryName: materialCategoryOptions[0]?.label || "",
          materialCategoryValue: formValue.materialCategoryValue || "",
          materialTwoCategoryName: materialTwoCategoryOptions[0]?.label || "",
          materialTwoCategoryValue: formValue.materialTwoCategoryValue || "",
          riskLevelAssessmentName: formValue.riskLevelAssessmentName || "",
          riskTypeName: formValue.riskTypeName || "",
          supplierUserId: formValue.supplierUserId || "",
        };

        // 编辑模式：调用 /view/pass 接口
        if (isEditMode && taskId) {
          console.log("【编辑暂存】调用 /view/pass 接口，taskId:", taskId);
          const passData: any = {
            flowStatus: 0, // 暂存状态
            flowCode: "SUPPLIER_RISK_REGISTER",
            skipType: "PASS",
            formNo: processCode,
            variable: {
              supplierUserId: formValue.supplierUserId,
            },
            srmSupplierRiskRegisterDTO: {
              ...submitData,
              id: editDataId, // 添加编辑数据的 id
            },
          };

          // businessStatus === 3 时，添加 message 字段（意见说明）
          if (businessStatus === 3) {
            passData.message = formValue.assessmentComments || "";
          }

          const response = await passFlow(taskId, passData);
          console.log("【暂存响应】", response);
          message.success(t("已暂存"));
        } else {
          // 新增模式：调用 /view/start 接口
          console.log("【新增暂存】调用 /view/start 接口");
          const newSubmitData = {
            flowStatus: 0,
            flowCode: "SUPPLIER_RISK_REGISTER",
            formNo: `${processCode}`,
            variable: {
              supplierUserId: formValue.supplierUserId,
            },
            srmSupplierRiskRegisterDTO: submitData,
          };

          console.log("【暂存数据】", newSubmitData);
          console.table(newSubmitData);

          // 调用 /view/start 接口发起流程
          const response = await startMeetingApproval(newSubmitData);
          console.log("【流程启动响应】", response);

          message.success(t("已暂存"));
        }
      }
    } catch (error) {
      console.error("暂存失败:", error);
      message.error(t("暂存失败"));
    } finally {
      setDraftLoading(false);
    }
  };

  // 处理Modal关闭（右上角删除按钮）
  const handleModalClose = useCallback(() => {
    formRef.current?.resetFields();
    onClose?.();
  }, [onClose]);

  // ==================== 处理通知供应商 ====================
  const handleNotifySupplier = useCallback(async () => {
    try {
      setNotifyLoading(true);

      // 从表单中获取必要的数据
      if (!formRef.current) {
        message.error(t("表单数据获取失败"));
        return;
      }
      const formValue = formRef.current.getFieldsValue();

      // 处理 fileIds：提取文件对象数组中的 uid，生成纯uid数组
      if (Array.isArray(formValue.fileIds) && formValue.fileIds.length > 0) {
        formValue.fileIds = formValue.fileIds
          .map((file: any) => file.uid)
          .filter((uid: any) => uid); // 过滤掉空值
      }

      // 准备通知数据，调用 /view/pass 接口
      if (taskId) {
        const notifyData = {
          flowCode: "SUPPLIER_RISK_REGISTER",
          skipType: "PASS",
          formNo: processCode,
          variable: {
            supplierUserId: formValue.supplierUserId,
          },
          srmSupplierRiskRegisterDTO: {
            ...formValue,
            id: editDataId,
            processingStatus: 2,
          },
        };

        console.log("【通知供应商】调用 /view/pass 接口，taskId:", taskId);
        const response = await passFlow(taskId, notifyData);
        console.log("【通知响应】", response);

        if (response?.data?.code === 0 || response?.data?.success) {
          message.success(t("已通知供应商"));
          // 调用父组件的回调函数，用于刷新列表
          onNotify?.();
          handleModalClose();
        } else {
          message.error(response?.data?.message || t("通知供应商失败"));
        }
      } else {
        message.error(t("任务ID不存在无法通知供应商"));
      }
    } catch (error) {
    } finally {
      setNotifyLoading(false);
    }
  }, [taskId, editDataId, processCode, onNotify, handleModalClose, t]);

  return (
    <>
      <Modal
        title={getModalTitle(
          t,
          processCode,
          isEditMode,
          readOnly,
          isNotifyMode,
        )}
        open={visible}
        width={1300}
        style={{ top: 20 }}
        onCancel={handleModalClose}
        className={styles.riskModal}
        footer={
          <div className={styles.modalFooter}>
            {isNotifyMode || businessStatus === 1 ? (
              <Space>
                <Button onClick={handleModalClose}>{tCommon("关闭")}</Button>
                <Button
                  type="primary"
                  loading={notifyLoading}
                  onClick={handleNotifySupplier}
                >
                  {t("通知供应商风险")}
                </Button>
              </Space>
            ) : businessStatus === 3 ? (
              <Space>
                <Button onClick={handleModalClose}>{tCommon("关闭")}</Button>
                <Button
                  type="primary"
                  loading={submitLoading}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.submit();
                    }
                  }}
                >
                  {tCommon("提交")}
                </Button>
              </Space>
            ) : readOnly ? (
              <Space>
                <Button onClick={handleModalClose}>{tCommon("关闭")}</Button>
              </Space>
            ) : (
              <Space>
                <Button loading={draftLoading} onClick={handleSaveDraft}>
                  {tCommon("暂存")}
                </Button>
                <Button
                  type="primary"
                  loading={submitLoading}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.submit();
                    }
                  }}
                >
                  {tCommon("提交")}
                </Button>
              </Space>
            )}
          </div>
        }
      >
        <div className={styles.modalBody}>
          <div style={{ display: "flex", gap: "20px" }}>
            {/* 左侧表单区域 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <DynamicForm
                ref={formRef}
                formItem={formItems}
                formValue={personnelInfo || {}}
                onFinish={handleSubmit}
                readOnly={readOnly || isNotifyMode || businessStatus === 1}
              />
            </div>

            {/* 右侧流程动态区域 - 当只读模式或通知模式且有历史数据时显示 */}
            {(readOnly || isNotifyMode) && timelineData.length > 0 && (
              <div style={{ width: "320px", flexShrink: 0 }}>
                <div
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1d2129",
                  }}
                >
                  {t("流程动态")}
                </div>
                <ProcessTimeline items={timelineData} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* 供应商选择弹框 */}
      <SelectSupplierModal
        open={supplierModalOpen}
        onClose={handleSupplierSelect}
        type="visitAuditRisk"
      />
    </>
  );
};

export default RegisterRiskModal;
